import React, { useState, useEffect, useMemo, useRef } from 'react';
import { api } from '../api';
import { Spinner, Modal, EstadoBadge, Nixie, ESTADO_META as ESTADO_UI, ESTADO_LABEL } from '../components/UI';
import { useAuth } from '../context/AuthContext';
import AsistenteIA from '../components/AsistenteIA';
import { Icono as IconoUI } from '../components/Iconos';
import { hoyISO, fechaISO } from '../utils';

// Hook para detectar ancho de pantalla
function useWindowW() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return w;
}

// Tokens del tema (variables de theme.css): en .legacy-light se redefinen en claro
const T = {
  primary: 'var(--plasma)', primaryText: 'var(--plasma)', primaryTint: 'var(--plasma-dim)',
  ink: 'var(--ink)', inkSoft: 'var(--ink-soft)', muted: 'var(--glass)',
  line: 'var(--mesh-soft)', field: 'var(--mesh)', canvas: 'var(--chassis)', tableHead: 'var(--well)',
  errorText: 'var(--danger)', errorBg: 'var(--danger-bg)', errorLine: 'var(--danger-line)',
  mono: 'var(--digit-font)', label: 'var(--label-font)',
};

// Estados: la única fuente es ESTADO_META de UI.js. Aquí solo se reexpone con
// la forma que ya consumían los tableros (`color` = punto sólido del semáforo).
export const ESTADO_META = Object.fromEntries(Object.entries(ESTADO_UI).map(([k, m]) => [k, {
  label: m.label, color: m.dot, text: m.text, bg: m.bg, border: m.border,
}]));

// Tipos de solicitud para el filtro del dashboard (comparten estructura y agregaciones).
// Comprobación se muestra aparte (usa comprobador/conceptoGasto), no entra aquí.
export const TIPO_SOLICITUD_META = {
  // 'pago' es la solicitud de gastos en general; se nombra así en el PDF y en las tarjetas por tipo
  pago:           { label: 'Gastos en General' },
  debito:         { label: 'Solicitud de Débito' },
  contribuciones: { label: 'Solicitud de Contribuciones' },
  activos_fijos:  { label: 'Solicitud de Activos Fijos' },
  mercancias_nacionales: { label: 'Adq. Mercancías Nacionales' },
  nomina:         { label: 'Pago de Nómina' },
  caja_chica:     { label: 'Comprobación Caja Chica' },
  reembolso_gastos: { label: 'Reembolso de Gastos' },
  complemento_importacion: { label: 'Complemento Costo Importación' },
  reembolso:      { label: 'Reembolso' },
  finiquito:      { label: 'Finiquito' },
};

// Montos. Resumen (tarjetas y totales) sin centavos; detalle (filas, tablas) con centavos.
export function fmt(n) { return (n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
export function fmtEntero(n) { return Math.round(n || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 }); }
// Solo para ejes de gráfica, donde no cabe la cifra completa.
export function fmtCompact(n) {
  n = n || 0;
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}
const fechaCorta = (iso, conAnio) => new Date(`${iso}T12:00:00`).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', ...(conAnio ? { year: 'numeric' } : {}) }).replace('.', '');

// Superficie compartida por los tableros
export const CARD = {
  boxShadow: 'var(--card-shadow)',
  background: 'var(--screws), var(--panel)',
  borderRadius: 6,
  border: `1px solid ${T.field}`,
  overflow: 'hidden',
};

export const CARD_TITLE = { padding: '12px 16px', borderBottom: `1px solid ${T.line}`, fontWeight: 600, fontSize: 13, color: T.ink, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 };
const CARD_NOTE = { fontSize: 12, fontWeight: 400, color: T.muted, whiteSpace: 'nowrap' };
const H3 = { margin: 0, fontFamily: T.label, fontSize: 15, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: T.ink, lineHeight: 1.15 };

const SELECT_STYLE = {
  padding: '7px 10px', borderRadius: 4, border: `1px solid ${T.field}`, background: 'var(--well)',
  fontSize: 12.5, color: T.ink, fontFamily: 'inherit', outline: 'none', cursor: 'pointer', minWidth: 130, maxWidth: 200,
};

const Vacio = ({ children, pad = 24 }) => (
  <div style={{ padding: pad, textAlign: 'center', color: T.muted, fontSize: 12.5 }}>{children}</div>
);

// Iconos dibujados (trazo de 1.75, mismo peso en todo el tablero)
const Icono = ({ d, size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{d}</svg>
);
const ICONO_LUPA = <><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>;
const ICONO_ALERTA = <><path d="M12 3 2 21h20L12 3Z" /><path d="M12 10v5" /><path d="M12 18h.01" /></>;

// ── Presets de fechas ──────────────────────────────────────────────────────
export const PRESETS = [
  { id: 'mes',  label: 'Este mes' },
  { id: '3m',   label: '3 meses' },
  { id: 'anio', label: 'Este año' },
  { id: 'todo', label: 'Todo' },
];

export function rangoPreset(id) {
  const hoy = new Date();
  const iso = (d) => fechaISO(d);
  if (id === 'mes')  return { desde: iso(new Date(hoy.getFullYear(), hoy.getMonth(), 1)), hasta: hoyISO() };
  if (id === '3m')   return { desde: iso(new Date(hoy.getFullYear(), hoy.getMonth() - 2, 1)), hasta: hoyISO() };
  if (id === 'anio') return { desde: iso(new Date(hoy.getFullYear(), 0, 1)), hasta: hoyISO() };
  return { desde: '', hasta: '' };
}

// Rango [domingo, sábado] de una semana del año, con la misma numeración que la
// columna SEMANA del Reporte Pipeline (semana 1 = la que contiene el 1 de enero).
export function rangoSemana(anio, num) {
  const iso = (d) => fechaISO(d);
  const inicio = new Date(anio, 0, 1);
  const domingo0 = new Date(anio, 0, 1 - inicio.getDay()); // domingo en o antes del 1 de enero
  const desde = new Date(domingo0); desde.setDate(domingo0.getDate() + (num - 1) * 7);
  const hasta = new Date(desde); hasta.setDate(desde.getDate() + 6);
  return { desde: iso(desde), hasta: iso(hasta) };
}

// Número de semana de una fecha, con la misma numeración que rangoSemana
// (la del Reporte Pipeline: semana 1 = la que contiene el 1 de enero).
export function numeroSemana(fecha = new Date()) {
  // Se compara a medianoche local y con los días redondeados: dejar la hora
  // dentro del cálculo hacía que un sábado (último día de la semana) el redondeo
  // hacia arriba cayera en la semana siguiente.
  const dia = new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate());
  const inicio = new Date(dia.getFullYear(), 0, 1);
  const dias = Math.round((dia - inicio) / 86400000);
  return Math.ceil((dias + inicio.getDay() + 1) / 7);
}

// "Semana 32 (2–8 ago)" — el rango de fechas evita tener que adivinar a qué
// semana corresponde cada número.
export function etiquetaSemana(anio, num) {
  const { desde, hasta } = rangoSemana(anio, num);
  const d = new Date(desde + 'T12:00:00');
  const h = new Date(hasta + 'T12:00:00');
  const mes = (x) => x.toLocaleDateString('es-MX', { month: 'short' }).replace('.', '');
  const rango = d.getMonth() === h.getMonth()
    ? `${d.getDate()}–${h.getDate()} ${mes(h)}`
    : `${d.getDate()} ${mes(d)} – ${h.getDate()} ${mes(h)}`;
  return `Semana ${num} (${rango})`;
}

// Tendencia contra el periodo anterior de la misma duración. Neutra a
// propósito: gastar más no es "bueno" (verde) ni "malo" (rojo) por sí mismo.
function Tendencia({ actual, previo, texto = 'vs periodo anterior' }) {
  if (previo == null || !(previo > 0) || actual == null) return null;
  const pct = ((actual - previo) / previo) * 100;
  return (
    <div style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>
      <span style={{ fontFamily: T.mono, color: T.inkSoft, display: 'inline-flex', alignItems: 'center', gap: 4 }}><IconoUI nombre={pct >= 0 ? 'sube' : 'baja'} size={11} stroke={2} />{Math.abs(pct).toFixed(1)}%</span> {texto}
    </div>
  );
}

// ── KPI ────────────────────────────────────────────────────────────────────
// `color` marca el estado con un punto junto a la etiqueta (el texto sigue
// siendo quien lo nombra). Con `onClick` la tarjeta es un botón que filtra.
export function KpiCard({ label, value, sub, color, onClick, active, prevMonto, rawMonto }) {
  const cuerpo = (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10 }}>
        {color && <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0 }} />}
        <span className="nx-label">{label}</span>
      </div>
      <div style={{ fontSize: 24, fontWeight: 500, color: T.ink, lineHeight: 1.1, letterSpacing: '-0.02em', fontFamily: T.mono }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: T.muted, marginTop: 6 }}>{sub}</div>}
      <Tendencia actual={rawMonto} previo={prevMonto} />
    </>
  );
  if (!onClick) return <div style={{ ...CARD, padding: 20 }}>{cuerpo}</div>;
  return (
    <button type="button" className="db-plain db-cell" aria-pressed={!!active} onClick={onClick}
      style={{ ...CARD, padding: 20, width: '100%', boxShadow: active ? `var(--card-shadow), inset 0 0 0 2px ${T.primary}` : CARD.boxShadow }}>
      {cuerpo}
    </button>
  );
}

// ── Lista de barras horizontales (clic = filtrar) ─────────────────────────
// Alerta de concentración: si un solo elemento (ej. proveedor) representa una
// porción demasiado grande del total, es un riesgo de dependencia, no solo un
// dato de ranking. Umbral 40%.
export function concentracion(lista, etiqueta = 'elemento') {
  const total = (lista || []).reduce((s, d) => s + d.monto, 0);
  const top = lista?.[0];
  if (!top || !top._id || total <= 0) return null;
  const pct = (top.monto / total) * 100;
  if (pct < 40) return null;
  return `"${top._id}" concentra ${pct.toFixed(0)}% del gasto de este ${etiqueta} en el periodo`;
}

// Las direcciones se guardan como "DIRECCION COMPRAS", "DIRECCION GENERAL"...
// El prefijo se repite en todas y el gráfico ya dice "por dirección", así que
// solo estorba al leer. Es cosmético: el valor guardado y el del filtro no cambian.
export const sinPrefijoDireccion = (v) => String(v || '').replace(/^DIRECCI[OÓ]N\s+/i, '');

// `etiqueta` solo cambia cómo se muestra cada fila; el valor con el que se
// filtra sigue siendo d._id. Sin `onToggle` las filas son solo lectura.
// `limite` muestra las primeras N (más las seleccionadas) y deja el resto a "Ver todas".
export function BarList({ titulo, data, activo, onToggle, onDrill, alerta, etiqueta = (v) => v, limite, pie }) {
  const [verTodas, setVerTodas] = useState(false);
  const max = Math.max(...data.map(d => d.monto), 1);
  const total = data.reduce((s, d) => s + d.monto, 0);
  // `activo` puede traer varios valores separados por coma (filtros multiselección)
  const seleccionados = String(activo || '').split(',').map(s => s.trim()).filter(Boolean);
  const recortada = limite && !verTodas && data.length > limite;
  const filas = recortada ? data.filter((d, i) => i < limite || seleccionados.includes(d._id)) : data;

  return (
    <div style={CARD}>
      <div style={CARD_TITLE}>
        <h3 style={H3}>{titulo}</h3>
        <span style={{ ...CARD_NOTE, fontFamily: T.mono, color: T.inkSoft }}>${fmtEntero(total)}</span>
      </div>
      {alerta && (
        <div role="note" style={{ margin: '10px 16px 0', padding: '8px 10px', borderRadius: 6, background: T.errorBg, border: `1px solid ${T.errorLine}`, fontSize: 12, color: T.errorText, fontWeight: 600, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <span style={{ marginTop: 1 }}><Icono d={ICONO_ALERTA} /></span>{alerta}
        </div>
      )}
      <div style={{ maxHeight: verTodas ? 380 : undefined, overflowY: verTodas ? 'auto' : undefined }}>
        {data.length === 0 ? <Vacio>Sin datos en el periodo</Vacio> : filas.map((d, i) => {
          const esActivo = seleccionados.includes(d._id);
          const pct = total > 0 ? (d.monto / total) * 100 : 0;
          const nombre = d._id ? etiqueta(d._id) : 'Sin asignar';
          const contenido = (
            <>
              {/* El nombre va completo en su propio renglón (las tarjetas son angostas
                  y con el monto al lado se cortaba en tres letras) */}
              <div style={{ fontSize: 12.5, fontWeight: esActivo ? 700 : 600, color: esActivo ? T.primaryText : T.ink, lineHeight: 1.3, overflowWrap: 'anywhere', marginBottom: 4 }}>
                {nombre}
              </div>
              {/* Si monto, porcentaje y solicitudes no caben juntos, las solicitudes bajan de renglón */}
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'baseline', columnGap: 10, rowGap: 2, marginBottom: 6 }}>
                <span style={{ fontSize: 12.5, fontWeight: 500, color: T.ink, fontFamily: T.mono, whiteSpace: 'nowrap' }}>
                  ${fmt(d.monto)} <span style={{ color: T.muted, fontSize: 11.5 }}>{pct.toFixed(1)}%</span>
                </span>
                <span style={{ fontSize: 11.5, color: T.muted, flexShrink: 0, textAlign: 'right', fontFamily: T.mono, whiteSpace: 'nowrap' }}>{d.count} sol.</span>
              </div>
              <div style={{ height: 6, background: T.tableHead, borderRadius: 3, overflow: 'hidden' }}>
                <div className="db-bar" style={{ transform: `scaleX(${d.monto / max})`, background: esActivo ? T.primary : 'var(--steel)', boxShadow: esActivo ? 'var(--glow)' : 'none' }} />
              </div>
            </>
          );
          return (
            <div key={d._id || i} className={onToggle ? 'db-row' : undefined} data-activo={esActivo}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 16px', borderBottom: `1px solid ${T.line}` }}>
              {onToggle && d._id ? (
                <button type="button" className="db-plain" style={{ flex: 1, minWidth: 0 }} aria-pressed={esActivo}
                  onClick={() => onToggle(d._id)}
                  title={esActivo ? 'Quitar filtro' : `Filtrar por "${nombre}"`}>
                  {contenido}
                </button>
              ) : <div style={{ flex: 1, minWidth: 0 }}>{contenido}</div>}
              {d._id && onDrill && (
                <button type="button" className="db-icon-btn" onClick={() => onDrill(d._id)}
                  aria-label={`Ver órdenes de ${nombre}`} title={`Ver órdenes de "${nombre}"`}>
                  <Icono d={ICONO_LUPA} />
                </button>
              )}
            </div>
          );
        })}
      </div>
      {limite && data.length > limite && (
        <button type="button" className="db-plain db-row" onClick={() => setVerTodas(v => !v)}
          style={{ padding: '9px 16px', fontSize: 12.5, fontWeight: 600, color: T.primaryText }}>
          {verTodas ? 'Ver solo las principales' : `Ver las ${data.length}`}
        </button>
      )}
      {pie}
    </div>
  );
}

// ── Dona por estado (clic = filtrar) ───────────────────────────────────────
// Sin `onToggle` es solo lectura (Mi Dashboard).
export function DonutEstados({ data, activo, onToggle }) {
  const total = data.reduce((s, d) => s + d.monto, 0);
  const totalCount = data.reduce((s, d) => s + d.count, 0);
  const R = 70, CX = 90, CY = 90, W = 22;

  let acc = 0;
  const arcos = data.filter(d => d.monto > 0).map(d => {
    const frac = total > 0 ? d.monto / total : 0;
    const a0 = acc * 2 * Math.PI - Math.PI / 2;
    acc += frac;
    const a1 = acc * 2 * Math.PI - Math.PI / 2;
    const large = frac > 0.5 ? 1 : 0;
    const x0 = CX + R * Math.cos(a0), y0 = CY + R * Math.sin(a0);
    const x1 = CX + R * Math.cos(a1), y1 = CY + R * Math.sin(a1);
    // círculo completo si es 100%
    const path = frac >= 0.999
      ? `M ${CX} ${CY - R} A ${R} ${R} 0 1 1 ${CX - 0.01} ${CY - R}`
      : `M ${x0} ${y0} A ${R} ${R} 0 ${large} 1 ${x1} ${y1}`;
    return { ...d, path, frac };
  });

  return (
    <div style={CARD}>
      <div style={CARD_TITLE}><h3 style={H3}>Distribución por estado</h3></div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', flexWrap: 'wrap' }}>
        <svg viewBox="0 0 180 180" style={{ width: 160, height: 160, flexShrink: 0 }} role="img"
          aria-label={`${totalCount} solicitudes por $${fmtEntero(total)}, repartidas por estado`}>
          {arcos.length === 0 && <circle cx={CX} cy={CY} r={R} fill="none" style={{ stroke: T.tableHead }} strokeWidth={W} />}
          {arcos.map(a => {
            const meta = ESTADO_META[a._id] || { color: T.muted };
            const dim = activo && activo !== a._id;
            return (
              <path key={a._id} d={a.path} fill="none" style={{ stroke: meta.color, transition: 'opacity 0.15s' }} strokeWidth={W}
                strokeLinecap="butt" opacity={dim ? 0.25 : 1}>
                <title>{`${meta.label || a._id}: $${fmt(a.monto)} (${(a.frac * 100).toFixed(1)}%)`}</title>
              </path>
            );
          })}
          <text x={CX} y={CY - 4} textAnchor="middle" style={{ fontSize: 22, fontWeight: 500, fill: T.ink, fontFamily: T.mono }}>{totalCount}</text>
          <text x={CX} y={CY + 16} textAnchor="middle" style={{ fontSize: 11.5, fill: T.muted, fontFamily: T.mono }}>${fmtEntero(total)}</text>
        </svg>
        <div style={{ flex: 1, minWidth: 180, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {Object.entries(ESTADO_META).map(([e, m]) => {
            const d = data.find(x => x._id === e) || { count: 0, monto: 0 };
            const fila = (
              <>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, fontWeight: 600, color: T.inkSoft }}>
                  <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%', background: m.color }} />
                  {m.label}
                </span>
                <span style={{ fontSize: 12, fontFamily: T.mono, color: T.muted }}>{d.count} · ${fmtEntero(d.monto)}</span>
              </>
            );
            const estilo = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '6px 10px', borderRadius: 6 };
            return onToggle ? (
              <button key={e} type="button" className="db-plain db-cell" aria-pressed={activo === e} onClick={() => onToggle(e)} style={estilo}>{fila}</button>
            ) : <div key={e} style={estilo}>{fila}</div>;
          })}
        </div>
      </div>
    </div>
  );
}

// ── Serie temporal (área) ──────────────────────────────────────────────────
export function AreaChart({ serie, granularidad, ancho = 720 }) {
  const [hover, setHover] = useState(null);
  const svgRef = useRef(null);
  const W = ancho, H = 230, PL = 56, PR = 16, PT = 18, PB = 34;

  const pts = useMemo(() => {
    const maxM = Math.max(...serie.map(s => s.monto), 1);
    const n = serie.length;
    return serie.map((s, i) => ({
      ...s,
      x: PL + (n > 1 ? (i / (n - 1)) * (W - PL - PR) : (W - PL - PR) / 2),
      y: PT + (1 - s.monto / maxM) * (H - PT - PB),
    }));
  }, [serie, W]);

  const maxM = Math.max(...serie.map(s => s.monto), 1);
  const etiqueta = (id) => {
    if (granularidad === 'dia') { const [, m, d] = id.split('-'); return `${d}/${m}`; }
    const [y, m] = id.split('-');
    const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return `${meses[parseInt(m, 10) - 1]} ${y.slice(2)}`;
  };

  const onMove = (e) => {
    if (!svgRef.current || pts.length === 0) return;
    const rect = svgRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * W;
    let best = 0, bd = Infinity;
    pts.forEach((p, i) => { const d = Math.abs(p.x - x); if (d < bd) { bd = d; best = i; } });
    setHover(best);
  };

  const paso = Math.max(1, Math.ceil(pts.length / 10));
  const area = pts.length > 1
    ? `M ${pts[0].x} ${H - PB} L ${pts.map(p => `${p.x} ${p.y}`).join(' L ')} L ${pts[pts.length - 1].x} ${H - PB} Z`
    : '';
  const linea = pts.length > 1 ? `M ${pts.map(p => `${p.x} ${p.y}`).join(' L ')}` : '';
  const hp = hover != null ? pts[hover] : null;

  return (
    <div style={CARD}>
      <div style={CARD_TITLE}>
        <h3 style={H3}>Evolución del gasto {granularidad === 'dia' ? '(diario)' : '(mensual)'}</h3>
        {hp && (
          <span style={{ ...CARD_NOTE, fontFamily: T.mono, color: T.ink }}>
            {etiqueta(hp._id)} · ${fmt(hp.monto)} · {hp.count} sol.
          </span>
        )}
      </div>
      {pts.length === 0 ? <Vacio pad={40}>Sin datos en el rango seleccionado</Vacio> : (
        <>
          <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }} aria-hidden="true"
            onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
            <defs>
              <linearGradient id="gradArea" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" style={{ stopColor: T.primary, stopOpacity: 0.28 }} />
                <stop offset="100%" style={{ stopColor: T.primary, stopOpacity: 0 }} />
              </linearGradient>
            </defs>
            {[0.25, 0.5, 0.75, 1].map(f => {
              const y = PT + (1 - f) * (H - PT - PB);
              return (
                <g key={f}>
                  <line x1={PL} y1={y} x2={W - PR} y2={y} style={{ stroke: T.line }} strokeWidth="1" />
                  <text x={PL - 7} y={y + 3.5} textAnchor="end" style={{ fontSize: 11.5, fill: T.muted, fontFamily: T.mono }}>
                    {fmtCompact(maxM * f)}
                  </text>
                </g>
              );
            })}
            <line x1={PL} y1={H - PB} x2={W - PR} y2={H - PB} style={{ stroke: T.field }} strokeWidth="1" />
            {pts.map((p, i) => (i % paso === 0 || i === pts.length - 1) && (
              <text key={p._id} x={p.x} y={H - PB + 17} textAnchor="middle" style={{ fontSize: 11.5, fill: T.muted, fontFamily: T.mono }}>
                {etiqueta(p._id)}
              </text>
            ))}
            {pts.length === 1 ? (
              <circle cx={pts[0].x} cy={pts[0].y} r="5" style={{ fill: T.primary }} />
            ) : (
              <>
                <path d={area} fill="url(#gradArea)" />
                <path d={linea} fill="none" style={{ stroke: T.primary, filter: 'drop-shadow(0 0 4px var(--plasma-line))' }} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
              </>
            )}
            {hp && (
              <g>
                <line x1={hp.x} y1={PT} x2={hp.x} y2={H - PB} style={{ stroke: T.inkSoft }} strokeWidth="1" strokeDasharray="4 3" opacity="0.5" />
                <circle cx={hp.x} cy={hp.y} r="5" style={{ fill: 'var(--panel)', stroke: T.primary }} strokeWidth="2" />
              </g>
            )}
          </svg>
          {/* La gráfica solo se lee con el mouse; la misma serie, como tabla, para lector de pantalla */}
          <table className="sr-only">
            <caption>Evolución del gasto {granularidad === 'dia' ? 'por día' : 'por mes'}</caption>
            <thead><tr><th scope="col">Periodo</th><th scope="col">Monto</th><th scope="col">Solicitudes</th></tr></thead>
            <tbody>{pts.map(p => <tr key={p._id}><td>{etiqueta(p._id)}</td><td>${fmt(p.monto)}</td><td>{p.count}</td></tr>)}</tbody>
          </table>
        </>
      )}
    </div>
  );
}

// ── Reparto del gasto: una gráfica con sus propios filtros ─────────────────
// Elige por qué dimensión repartir el gasto y, al presionar una barra, la
// gráfica se filtra a ese valor y pasa a la siguiente dimensión (compañía →
// sucursal → dirección…), con una ruta para regresar. Sus filtros son SOLO de la
// gráfica: el resto del tablero no se mueve, pero ella sí respeta los filtros
// generales de arriba. Los datos salen del mismo /dashboard/analytics, así que
// cada reparto suma lo mismo que el total del tablero.
const DIMS_REPARTO = [
  { id: 'empresa', label: 'Compañía', campo: 'porEmpresa', filtro: 'empresa' },
  { id: 'sucursal', label: 'Sucursal', campo: 'porSucursal', filtro: 'sucursal' },
  { id: 'direccion', label: 'Dirección', campo: 'porDireccion', filtro: 'direccion', etiqueta: sinPrefijoDireccion },
  { id: 'cuentaAgrupadora', label: 'Categoría', campo: 'porCuentaAgrupadora', filtro: 'cuentaAgrupadora' },
  { id: 'motivo', label: 'Motivo', campo: 'porMotivo', filtro: 'motivo' },
  { id: 'proveedor', label: 'Proveedor', campo: 'topProveedores', filtro: 'proveedor' },
  { id: 'tipo', label: 'Tipo', campo: 'porTipo', filtro: 'tipo', etiqueta: t => (TIPO_SOLICITUD_META[t] || {}).label || t },
  // El backend no filtra por solicitante: se puede ver, pero sus barras no filtran
  { id: 'solicitante', label: 'Solicitante', campo: 'porSolicitante', filtro: null },
];
const TOP_REPARTO = 10;

export function RepartoGasto({ dataGlobal, filtrosGlobal, recarga, onAplicar, isMobile }) {
  const [dim, setDim] = useState('empresa');
  const [locales, setLocales] = useState([]); // [{ filtro, valor, dimLabel, etiqueta }]
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(false);
  const [verTodos, setVerTodos] = useState(false);
  const [hover, setHover] = useState(null);
  const cache = useRef(new Map());

  // Filtros generales + los de la gráfica (los de la gráfica se suman, no se reemplazan)
  const qs = useMemo(() => {
    const f = { ...filtrosGlobal };
    for (const l of locales) f[l.filtro] = l.valor;
    return Object.entries(f).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
  }, [filtrosGlobal, locales]);

  // Si cambian los filtros generales, la gráfica arranca de cero
  const firmaGlobal = JSON.stringify(filtrosGlobal);
  useEffect(() => { setLocales([]); cache.current.clear(); }, [firmaGlobal, recarga]);

  useEffect(() => {
    if (!locales.length) { setDatos(dataGlobal); setError(false); return undefined; }
    if (cache.current.has(qs)) { setDatos(cache.current.get(qs)); return undefined; }
    let vivo = true;
    setCargando(true);
    api.get(`/dashboard/analytics?${qs}`)
      .then(d => { if (!vivo) return; cache.current.set(qs, d); setDatos(d); setError(false); })
      .catch(() => vivo && setError(true))
      .finally(() => vivo && setCargando(false));
    return () => { vivo = false; };
  }, [qs, locales.length, dataGlobal]);

  const usados = new Set([...locales.map(l => l.filtro), ...Object.entries(filtrosGlobal || {}).filter(([, v]) => v).map(([k]) => k)]);
  // Si la dimensión elegida ya quedó filtrada arriba, se pasa a la primera libre
  const libre = (x) => !x.filtro || !usados.has(x.filtro);
  const d = DIMS_REPARTO.find(x => x.id === dim && libre(x)) || DIMS_REPARTO.find(libre);
  const nombre = (v) => (v == null || v === '' ? 'Sin asignar' : d.etiqueta ? d.etiqueta(v) : v);
  // "Por tipo" el backend devuelve siempre todos los tipos: con un filtro de tipo
  // (general o de la gráfica) se recorta a esos, para que sume el total
  const tiposActivos = String([filtrosGlobal?.tipo, ...locales.filter(l => l.filtro === 'tipo').map(l => l.valor)].filter(Boolean).pop() || '')
    .split(',').map(s => s.trim()).filter(Boolean);
  const filas = ((datos && datos[d.campo]) || [])
    .filter(r => r.count > 0 || r.monto)
    .filter(r => d.id !== 'tipo' || !tiposActivos.length || tiposActivos.includes(r._id))
    .sort((a, b) => b.monto - a.monto);
  const total = datos?.totales?.monto || filas.reduce((s, r) => s + (r.monto || 0), 0);
  const solicitudes = datos?.totales?.count || 0;
  const visibles = verTodos ? filas : filas.slice(0, TOP_REPARTO);
  const resto = verTodos ? [] : filas.slice(TOP_REPARTO);
  const otros = resto.length ? { _id: '__otros', monto: resto.reduce((s, r) => s + r.monto, 0), count: resto.reduce((s, r) => s + r.count, 0), n: resto.length } : null;
  const max = Math.max(1, ...visibles.map(r => r.monto), otros?.monto || 0);
  const puedeFiltrar = (r) => d.filtro && r._id != null && r._id !== '' && r._id !== '__otros';

  // Al presionar una barra: se filtra la gráfica y se pasa a la siguiente dimensión libre
  const bajar = (r) => {
    if (!puedeFiltrar(r)) return;
    const nuevos = [...locales, { filtro: d.filtro, valor: r._id, dimLabel: d.label, etiqueta: nombre(r._id) }];
    setLocales(nuevos);
    const tomados = new Set([...usados, d.filtro]);
    const i = DIMS_REPARTO.findIndex(x => x.id === d.id);
    const siguiente = [...DIMS_REPARTO.slice(i + 1), ...DIMS_REPARTO.slice(0, i)].find(x => x.filtro && !tomados.has(x.filtro));
    if (siguiente) setDim(siguiente.id);
    setVerTodos(false); setHover(null);
  };
  const regresarA = (n) => { setLocales(l => l.slice(0, n)); setVerTodos(false); };

  const pct = (m) => (total > 0 ? (m / total) * 100 : 0);
  const fila = (r, i) => {
    const esOtros = r._id === '__otros';
    const clic = !esOtros && puedeFiltrar(r);
    const enHover = hover === i;
    const etiqueta = esOtros ? `Otros ${r.n} ${r.n === 1 ? 'valor' : 'valores'}` : nombre(r._id);
    const siguiente = (() => {
      const tomados = new Set([...usados, d.filtro]);
      const j = DIMS_REPARTO.findIndex(x => x.id === d.id);
      return [...DIMS_REPARTO.slice(j + 1), ...DIMS_REPARTO.slice(0, j)].find(x => x.filtro && !tomados.has(x.filtro));
    })();
    const ayuda = `${etiqueta}: $${fmt(r.monto)} (${pct(r.monto).toFixed(1)}% del total) · ${r.count} ${r.count === 1 ? 'solicitud' : 'solicitudes'}${r.count ? ` · promedio $${fmt(r.monto / r.count)}` : ''}${clic && siguiente ? `. Presiona para ver su reparto por ${siguiente.label.toLowerCase()}.` : ''}`;
    // Montos en una columna de ancho fijo: todas las pistas miden lo mismo y las barras se comparan bien
    const contenido = (
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(140px, 26%) 1fr 170px', alignItems: 'center', gap: isMobile ? 4 : 14 }}>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: esOtros ? T.muted : T.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }} title={etiqueta}>{etiqueta}</span>
        <div style={{ height: 14, background: 'var(--hair-soft)', borderRadius: '0 4px 4px 0', overflow: 'hidden', ...(isMobile ? { order: 3 } : {}) }}>
          <div className="db-bar" style={{ transform: `scaleX(${r.monto / max})`, background: esOtros ? 'var(--steel)' : 'var(--brand)', borderRadius: '0 4px 4px 0', opacity: hover == null || enHover ? 1 : 0.45, transition: 'transform .45s cubic-bezier(0.22, 1, 0.36, 1), opacity .12s' }} />
        </div>
        <span style={{ fontFamily: T.mono, fontSize: 12.5, color: T.ink, whiteSpace: 'nowrap', textAlign: 'right' }}>
          ${fmt(r.monto)} <span style={{ color: T.muted, fontSize: 11.5 }}>{pct(r.monto).toFixed(1)}%</span>
        </span>
      </div>
    );
    const clave = r._id ?? `sin-${i}`;
    const props = { onMouseEnter: () => setHover(i), onMouseLeave: () => setHover(null), onFocus: () => setHover(i), onBlur: () => setHover(null), title: ayuda };
    const estilo = { padding: '7px 16px', borderBottom: `1px solid ${T.line}`, background: enHover ? 'var(--hover)' : 'transparent' };
    return clic
      ? <button type="button" key={clave} className="db-plain" {...props} style={estilo} onClick={() => bajar(r)} aria-label={ayuda}>{contenido}</button>
      : <div key={clave} {...props} style={{ ...estilo, cursor: 'default' }}>{contenido}</div>;
  };
  const enfocada = hover != null ? (hover === -1 ? otros : visibles[hover]) : null;

  return (
    <div style={CARD}>
      <div style={{ ...CARD_TITLE, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0 }}>
          <h3 style={H3}>Reparto del gasto</h3>
          <div style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>
            Total <span style={{ fontFamily: T.mono, color: T.ink }}>${fmt(total)}</span> · {solicitudes} {solicitudes === 1 ? 'solicitud' : 'solicitudes'}
            {d.filtro ? ' · presiona una barra para ver su desglose' : ' · solo consulta: no se filtra por solicitante'}
          </div>
        </div>
        {/* Por qué dimensión repartir: el filtro principal, dentro de la gráfica */}
        {isMobile ? (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: T.muted }}>
            Ver por
            <select value={d.id} onChange={e => { setDim(e.target.value); setVerTodos(false); }} style={SELECT_STYLE}>
              {DIMS_REPARTO.map(x => <option key={x.id} value={x.id} disabled={x.filtro && usados.has(x.filtro)}>{x.label}</option>)}
            </select>
          </label>
        ) : (
          <div role="group" aria-label="Ver el reparto por" style={{ display: 'flex', flexWrap: 'wrap', gap: 2, background: T.canvas, border: `1px solid ${T.line}`, padding: 2, borderRadius: 6 }}>
            {DIMS_REPARTO.map(x => {
              const activo = d.id === x.id;
              const bloqueado = x.filtro && usados.has(x.filtro);
              return (
                <button type="button" key={x.id} onClick={() => { setDim(x.id); setVerTodos(false); }} aria-pressed={activo} disabled={bloqueado}
                  title={bloqueado ? `Ya está filtrada por ${x.label.toLowerCase()}` : `Ver el reparto por ${x.label.toLowerCase()}`}
                  style={{
                    padding: '5px 11px', borderRadius: 4, border: 'none', cursor: bloqueado ? 'default' : 'pointer', fontSize: 12.5, fontWeight: 600,
                    background: activo ? 'var(--panel-hi)' : 'transparent', color: activo ? T.primaryText : bloqueado ? 'var(--steel)' : T.inkSoft,
                    boxShadow: activo ? 'inset 0 -2px var(--plasma)' : 'none',
                  }}>
                  {x.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Filtros de la gráfica: la ruta de lo que se ha ido presionando */}
      {locales.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, padding: '10px 16px', borderBottom: `1px solid ${T.line}`, background: 'var(--panel-hi)' }}>
          <button type="button" className="db-chip" onClick={() => regresarA(0)} title="Quitar los filtros de la gráfica">Todo</button>
          {locales.map((l, i) => (
            <React.Fragment key={`${l.filtro}-${i}`}>
              <span aria-hidden="true" style={{ color: T.muted }}>›</span>
              <button type="button" className="db-chip" onClick={() => regresarA(i + 1)} aria-current={i === locales.length - 1 ? 'true' : undefined}
                title={i === locales.length - 1 ? `${l.dimLabel}: ${l.etiqueta}` : `Regresar a ${l.etiqueta}`}>
                <span style={{ color: T.muted, fontWeight: 400 }}>{l.dimLabel}:</span> {l.etiqueta}
              </button>
            </React.Fragment>
          ))}
          {onAplicar && (
            <button type="button" className="db-icon-btn" onClick={() => { onAplicar(locales); setLocales([]); }}
              style={{ marginLeft: 'auto', width: 'auto', padding: '0 10px', fontSize: 12.5, fontWeight: 600 }}
              title="Aplica estos filtros a todo el tablero">
              Aplicar al tablero
            </button>
          )}
        </div>
      )}

      <div style={{ opacity: cargando ? 0.55 : 1, transition: 'opacity .15s' }} aria-busy={cargando}>
        {error ? <Vacio pad={32}>No se pudo cargar el reparto. Intenta de nuevo.</Vacio>
          : !datos ? <div style={{ padding: 32, display: 'flex', justifyContent: 'center' }}><Spinner /></div>
          : filas.length === 0 ? <Vacio pad={32}>Sin gasto con estos filtros.</Vacio>
          : (
            <>
              {visibles.map(fila)}
              {otros && fila(otros, -1)}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '9px 16px', fontSize: 12, color: T.muted, minHeight: 36 }}>
                <span aria-live="polite" style={{ fontFamily: enfocada ? T.mono : undefined, color: enfocada ? T.ink : T.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                  {enfocada
                    ? `${enfocada._id === '__otros' ? `Otros ${enfocada.n}` : nombre(enfocada._id)} · ${enfocada.count} sol. · promedio $${fmt(enfocada.count ? enfocada.monto / enfocada.count : 0)}`
                    : `${filas.length} ${filas.length === 1 ? 'valor' : 'valores'} por ${d.label.toLowerCase()}`}
                </span>
                {filas.length > TOP_REPARTO && (
                  <button type="button" className="db-icon-btn" onClick={() => setVerTodos(v => !v)} style={{ width: 'auto', padding: '0 10px', fontSize: 12.5, fontWeight: 600, flexShrink: 0 }}>
                    {verTodos ? `Ver solo los ${TOP_REPARTO} principales` : `Ver los ${filas.length}`}
                  </button>
                )}
              </div>
              {/* La misma información como tabla, para lector de pantalla */}
              <table className="sr-only">
                <caption>Reparto del gasto por {d.label.toLowerCase()}{locales.length ? `, filtrado por ${locales.map(l => `${l.dimLabel} ${l.etiqueta}`).join(', ')}` : ''}</caption>
                <thead><tr><th scope="col">{d.label}</th><th scope="col">Monto</th><th scope="col">Porcentaje</th><th scope="col">Solicitudes</th></tr></thead>
                <tbody>{filas.map((r, i) => <tr key={r._id ?? `sin-${i}`}><td>{nombre(r._id)}</td><td>${fmt(r.monto)}</td><td>{pct(r.monto).toFixed(1)}%</td><td>{r.count}</td></tr>)}</tbody>
              </table>
            </>
          )}
      </div>
    </div>
  );
}

// ── Métricas de proceso: antigüedad, tiempos de ciclo y ahorro ──────────────
// Una sola rampa de acero que se aclara con la edad; el rojo queda para lo
// que ya es un problema (más de 15 días, o un pago programado que ya venció).
const AGING_META = [
  { rango: '0-3',  label: '0–3 días',       color: 'var(--mesh)' },
  { rango: '4-7',  label: '4–7 días',       color: 'var(--steel)' },
  { rango: '8-15', label: '8–15 días',      color: 'var(--glass)' },
  { rango: '15+',  label: 'Más de 15 días', color: 'var(--danger)', critico: true },
];

// Buckets de proyección de pagos (por_pagar según fechaProgramacion): a
// diferencia de los de antigüedad, miran hacia adelante en vez de hacia atrás.
// Lo que toca pagar ya (esta semana) se enciende en naranja: es la acción inmediata.
const PROYECCION_META = [
  { rango: 'atrasado',       label: 'Atrasado',             color: 'var(--danger)', critico: true },
  { rango: 'esta_semana',    label: 'Esta semana',          color: T.primary },
  { rango: 'proxima_semana', label: 'Próxima semana',       color: 'var(--glass)' },
  { rango: 'este_mes',       label: 'Resto del mes',        color: 'var(--steel)' },
  { rango: 'mas_adelante',   label: 'Más adelante',         color: 'var(--mesh)' },
  { rango: 'sin_fecha',      label: 'Sin fecha programada', color: 'var(--mesh)' },
];

// Suma de uno o varios tramos de una tarjeta ({count, monto}).
const tramos = (data, ...rangos) => (data || []).filter(r => rangos.includes(r.rango))
  .reduce((acc, r) => ({ count: acc.count + (r.count || 0), monto: acc.monto + (r.monto || 0) }), { count: 0, monto: 0 });

// Cifra de encabezado de una tarjeta de "Al día de hoy": lo primero que se lee.
function Cabeza({ etiqueta, children, nota }) {
  return (
    <div style={{ padding: '16px 16px 4px' }}>
      <div className="nx-label" style={{ marginBottom: 10 }}>{etiqueta}</div>
      <div style={{ lineHeight: 1.05 }}>{children}</div>
      {nota && <div style={{ fontSize: 12.5, color: T.inkSoft, marginTop: 8 }}>{nota}</div>}
    </div>
  );
}

// Con `onDrill`, cada tramo con solicitudes es un botón que abre su lista.
function Buckets({ meta, data, vacio, onDrill }) {
  const rows = meta.map(m => ({ ...m, ...(data || []).find(r => r.rango === m.rango) }));
  const total = rows.reduce((s, r) => s + (r.count || 0), 0);
  const max = Math.max(...rows.map(r => r.count || 0), 1);
  if (total === 0) return <Vacio pad={14}>{vacio}</Vacio>;
  return rows.map(r => {
    const n = r.count || 0;
    const alerta = r.critico && n > 0;
    const cuerpo = (
      <>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12.5, marginBottom: 4 }}>
          <span style={{ color: alerta ? T.errorText : T.inkSoft, fontWeight: alerta ? 700 : 600 }}>{r.label}</span>
          <span style={{ fontFamily: T.mono, color: alerta ? T.errorText : n > 0 ? T.ink : T.muted, fontWeight: 500 }}>
            {n} · ${fmt(r.monto || 0)}
          </span>
        </div>
        <div style={{ height: 6, background: T.tableHead, borderRadius: 3, overflow: 'hidden' }}>
          <div className="db-bar" style={{ transform: `scaleX(${n / max})`, background: r.color }} />
        </div>
      </>
    );
    if (!onDrill || n === 0) return <div key={r.rango}>{cuerpo}</div>;
    return (
      <button key={r.rango} type="button" className="db-plain db-row" onClick={() => onDrill(r.rango, r.label)}
        title={`Ver las ${n} solicitudes: ${r.label}`} style={{ margin: '-4px -6px', padding: '4px 6px', width: 'calc(100% + 12px)', borderRadius: 4 }}>
        {cuerpo}
      </button>
    );
  });
}

export function AgingCard({ data, nota = 'sin resolver', onDrill }) {
  const total = (data || []).reduce((s, r) => s + (r.count || 0), 0);
  const viejas = tramos(data, '15+');
  const todo = tramos(data, '0-3', '4-7', '8-15', '15+');
  return (
    <div style={CARD}>
      <div style={CARD_TITLE}>
        <h3 style={H3}>Validación contable por antigüedad</h3>
        <span style={CARD_NOTE}><span style={{ fontFamily: T.mono }}>{total}</span> {nota}</span>
      </div>
      {total > 0 && (
        <Cabeza etiqueta="Con más de 15 días" nota={<><span style={{ fontFamily: T.mono }}>{todo.count}</span> esperando validación · <span style={{ fontFamily: T.mono }}>${fmtEntero(todo.monto)}</span></>}>
          <Nixie value={String(viejas.count)} size={34} alerta={viejas.count > 0} label={`${viejas.count} solicitudes con más de 15 días esperando validación`} />
        </Cabeza>
      )}
      <div style={{ padding: '14px 16px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Buckets meta={AGING_META} data={data} vacio="Nada esperando validación contable." onDrill={onDrill} />
      </div>
    </div>
  );
}

// Cierre de pagos: lo que ya debió pagarse y no quedó cerrado (pagado con su
// comprobante adjunto). La regla vive en el backend (utils/cierre.js); aquí
// solo se avisa y se manda a la pantalla "Cierre de pagos" para resolverlo.
const CIERRE_META = [
  { id: 'vencida_sin_comprobante', label: 'Fecha vencida, sin comprobante' },
  { id: 'comprobante_sin_marcar', label: 'Comprobante sin marcar pagada' },
  { id: 'pagada_sin_comprobante', label: 'Pagada sin comprobante' },
];
function CierrePagosCard({ data, onAbrir }) {
  if (!data) return null;
  const { resumen: r, tiempos: t } = data;
  const mediana = t?.registroComprobanteDias?.mediana;
  return (
    <div style={CARD}>
      <div style={CARD_TITLE}>
        <h3 style={H3}>Cierre de pagos</h3>
        {r.todoCerrado ? <span style={CARD_NOTE}>al día</span> : <span style={CARD_NOTE}><span style={{ fontFamily: T.mono }}>{r.pendientes}</span> sin cerrar</span>}
      </div>
      {r.todoCerrado ? (
        <div role="status" style={{ padding: '18px 16px 16px', display: 'flex', gap: 12, alignItems: 'center' }}>
          <IconoUI nombre="check" size={24} style={{ color: 'var(--ok)', flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: 600, fontSize: 14 }}>Todos los pagos están cerrados</div>
            <div style={{ fontSize: 12.5, color: T.inkSoft, marginTop: 3 }}>Pagados con su comprobante y sin fechas vencidas por registrar.</div>
          </div>
        </div>
      ) : (
        <>
          <Cabeza etiqueta="Pagos sin cerrar" nota={<><span style={{ fontFamily: T.mono }}>${fmtEntero(r.montoMXN)}</span> netos</>}>
            <Nixie value={String(r.pendientes)} size={34} alerta={(r.porCaso.vencida_sin_comprobante?.folios || 0) > 0} label={`${r.pendientes} pagos sin cerrar`} />
          </Cabeza>
          <div style={{ padding: '10px 16px 4px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {CIERRE_META.map(m => {
              const v = r.porCaso[m.id] || { folios: 0, montoMXN: 0 };
              return (
                <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12.5 }}>
                  <span style={{ color: v.folios ? T.inkSoft : T.muted, fontWeight: 600 }}>{m.label}</span>
                  <span style={{ fontFamily: T.mono, color: v.folios ? T.ink : T.muted }}>{v.folios} · ${fmtEntero(v.montoMXN)}</span>
                </div>
              );
            })}
          </div>
        </>
      )}
      <div style={{ padding: '10px 16px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', borderTop: `1px solid ${T.line}`, marginTop: 8 }}>
        <span style={{ fontSize: 12, color: T.muted }}>
          {mediana != null ? <>Del pago al comprobante: <span style={{ fontFamily: T.mono, color: T.ink }}>{mediana} {mediana === 1 ? 'día' : 'días'}</span> (mediana, 90 días)</> : 'Sin pagos recientes para medir el registro.'}
        </span>
        {onAbrir && <button type="button" onClick={onAbrir} className="db-icon-btn" style={{ width: 'auto', padding: '0 10px', fontSize: 12.5, fontWeight: 600 }}>Ver cierre de pagos →</button>}
      </div>
    </div>
  );
}

// Backlog de pago = ya aprobado o ya programado, pero todavía sin pagar: es
// dinero comprometido que sigue sin salir. Antigüedad medida desde que entró
// a su estado actual (ver agingBacklog en el backend), no desde su creación.
function BacklogPagoCard({ data, aprobada, porPagar, onDrill }) {
  const total = (data || []).reduce((s, r) => s + (r.count || 0), 0);
  const a = aprobada || { count: 0, monto: 0 };
  const p = porPagar || { count: 0, monto: 0 };
  const caja = (estado, titulo, v) => (
    <div style={{ flex: 1, minWidth: 0, padding: '10px 12px', borderRadius: 4, background: 'var(--well)', border: `1px solid ${T.line}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.inkSoft, fontWeight: 600 }}>
        <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: ESTADO_META[estado].color, flexShrink: 0 }} />
        {titulo}
      </div>
      <div style={{ fontFamily: T.mono, fontWeight: 500, fontSize: 15, color: T.ink, marginTop: 4 }}>${fmtEntero(v.monto)}</div>
      <div style={{ fontSize: 12, color: T.muted }}><span style={{ fontFamily: T.mono }}>{v.count}</span> solicitud{v.count !== 1 ? 'es' : ''}</div>
    </div>
  );
  return (
    <div style={CARD}>
      <div style={CARD_TITLE}>
        <h3 style={H3}>Backlog de pago</h3>
        <span style={CARD_NOTE}><span style={{ fontFamily: T.mono }}>{total}</span> sin pagar</span>
      </div>
      {total > 0 && (
        <Cabeza etiqueta="Comprometido sin pagar">
          <Nixie value={`$${fmtEntero(a.monto + p.monto)}`} size={30} tubos={false} label={`Comprometido sin pagar: $${fmtEntero(a.monto + p.monto)} MXN`} />
        </Cabeza>
      )}
      <div style={{ display: 'flex', gap: 10, padding: '14px 16px 0', flexWrap: 'wrap' }}>
        {caja('aprobada', 'Aprobada, sin programar', a)}
        {caja('por_pagar', 'Programada, sin pagar', p)}
      </div>
      <div style={{ padding: '14px 16px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 12, color: T.muted }}>Días en su estado actual</div>
        <Buckets meta={AGING_META} data={data} vacio="Nada aprobado pendiente de pago." onDrill={onDrill} />
      </div>
    </div>
  );
}

// Proyección de pagos: de lo YA programado (por_pagar), cuándo debe salir
// según su fechaProgramacion. Visibilidad hacia adelante (cuánto se pagará
// esta semana/mes), no solo hacia atrás como el resto de tarjetas.
function ProyeccionPagoCard({ data, onDrill }) {
  const total = (data || []).reduce((s, r) => s + (r.count || 0), 0);
  const atrasado = tramos(data, 'atrasado');
  const semana = tramos(data, 'atrasado', 'esta_semana');
  return (
    <div style={CARD}>
      <div style={CARD_TITLE}>
        <h3 style={H3}>Proyección de pagos</h3>
        <span style={CARD_NOTE}><span style={{ fontFamily: T.mono }}>{total}</span> programadas</span>
      </div>
      {total > 0 && (
        <Cabeza etiqueta="A fondear esta semana" nota={atrasado.count > 0
          ? <span style={{ color: T.errorText, fontWeight: 600 }}>Incluye <span style={{ fontFamily: T.mono }}>${fmtEntero(atrasado.monto)}</span> atrasado (<span style={{ fontFamily: T.mono }}>{atrasado.count}</span>)</span>
          : <><span style={{ fontFamily: T.mono }}>{semana.count}</span> pago{semana.count !== 1 ? 's' : ''} · nada atrasado</>}>
          <Nixie value={`$${fmtEntero(semana.monto)}`} size={34} alerta={atrasado.count > 0} label={`A fondear esta semana: $${fmtEntero(semana.monto)} MXN${atrasado.count ? `, incluye $${fmtEntero(atrasado.monto)} atrasado` : ''}`} />
        </Cabeza>
      )}
      <div style={{ padding: '14px 16px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Buckets meta={PROYECCION_META} data={data} vacio="No hay pagos programados pendientes." onDrill={onDrill} />
      </div>
    </div>
  );
}

function CicloCard({ ciclo }) {
  const c = ciclo || {};
  const fila = (label, dias, fuerte) => (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, padding: '10px 0', borderBottom: `1px solid ${T.line}` }}>
      <span style={{ fontSize: 12.5, color: fuerte ? T.ink : T.inkSoft, fontWeight: fuerte ? 600 : 500 }}>{label}</span>
      <span style={{ fontFamily: T.mono, fontWeight: 500, fontSize: 15, color: dias == null ? T.muted : T.ink }}>
        {dias == null ? '—' : `${dias} días`}
      </span>
    </div>
  );
  return (
    <div style={CARD}>
      <div style={CARD_TITLE}>
        <h3 style={H3}>Tiempos de ciclo</h3>
        <span style={CARD_NOTE}><span style={{ fontFamily: T.mono }}>{c.muestras || 0}</span> pagos analizados</span>
      </div>
      <div style={{ padding: '4px 16px 14px' }}>
        {fila('Creada → Aprobada', c.aprobacionDias)}
        {fila('Aprobada → Pagada', c.pagoDias)}
        {fila('Ciclo completo', c.totalDias, true)}
        <div style={{ fontSize: 12, color: T.muted, marginTop: 10, lineHeight: 1.45 }}>Promedio de las solicitudes pagadas del periodo, según su historial de estados.</div>
      </div>
    </div>
  );
}

// Desempeño por aprobador: cuántas solicitudes ha aprobado cada quien y cuánto
// tarda en promedio desde que se creó la solicitud — para detectar cuellos de
// botella puntuales (a diferencia de CicloCard, que solo da el promedio general).
function AprobadoresCard({ data }) {
  const rows = data || [];
  const th = { padding: '8px 12px', whiteSpace: 'nowrap', textAlign: 'right' };
  const td = { padding: '9px 12px', fontFamily: T.mono, fontWeight: 500, fontSize: 12.5, textAlign: 'right', whiteSpace: 'nowrap' };
  const num = (v, alerta) => ({ ...td, color: alerta ? T.errorText : v ? T.ink : T.muted });
  return (
    <div style={CARD}>
      <div style={CARD_TITLE}>
        <h3 style={H3}>Desempeño por aprobador</h3>
        <span style={CARD_NOTE}><span style={{ fontFamily: T.mono }}>{rows.length}</span> aprobador{rows.length !== 1 ? 'es' : ''}</span>
      </div>
      {/* Las columnas miden periodos distintos a propósito: "aprobadas" y
          "días" son del rango filtrado; "en cola total" es la foto de hoy, que
          es donde se ve el rezago viejo. */}
      <div style={{ fontSize: 12, color: T.muted, padding: '10px 16px', lineHeight: 1.45, maxWidth: '80ch' }}>
        En cola = lo que espera su acción. <b style={{ color: T.inkSoft }}>Periodo</b> según el filtro de fechas; <b style={{ color: T.inkSoft }}>total</b> sin importar cuándo llegó. En rojo: la cola total supera lo aprobado, o tarda más de 5 días.
      </div>
      {rows.length === 0 ? <Vacio>Sin aprobaciones en el periodo</Vacio> : (
        <div style={{ maxHeight: 360, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th scope="col" style={{ ...th, textAlign: 'left', paddingLeft: 16 }}>Aprobador</th>
                <th scope="col" style={th}>Aprobadas</th>
                <th scope="col" style={th}>En cola · periodo</th>
                <th scope="col" style={th}>En cola · total</th>
                <th scope="col" style={{ ...th, paddingRight: 16 }}>Días prom.</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r._id || i}>
                  <td style={{ padding: '9px 12px 9px 16px', fontSize: 12.5, fontWeight: 600, color: T.ink, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r._id}>{r._id}</td>
                  <td style={num(r.count)}>{r.count || 0}</td>
                  <td style={num(r.colaPeriodo)}>{r.colaPeriodo || 0}</td>
                  {/* El total es lo que delata el rezago: se resalta cuando supera lo aprobado */}
                  <td style={num(r.cola, (r.cola || 0) > (r.count || 0))}>{r.cola || 0}</td>
                  <td style={{ ...num(r.diasProm, r.diasProm > 5), paddingRight: 16 }}>{r.diasProm != null ? `${r.diasProm} d` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AhorroCard({ ahorro }) {
  const a = ahorro || {};
  return (
    <div style={CARD}>
      <div style={CARD_TITLE}>
        <h3 style={H3}>Ahorro por cotizar</h3>
        <span style={CARD_NOTE}><span style={{ fontFamily: T.mono }}>{a.compras || 0}</span> comparativos</span>
      </div>
      <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {(a.compras || 0) === 0 ? <Vacio pad={14}>Sin comparativos con ganador en el periodo</Vacio> : (
          <>
            <div style={{ fontFamily: T.mono, fontSize: 26, fontWeight: 500, color: T.ink, letterSpacing: '-0.02em' }}>${fmtEntero(a.total)}</div>
            <div style={{ fontSize: 12.5, color: T.muted, lineHeight: 1.5 }}>
              ahorrados frente a la cotización más cara en <b style={{ color: T.inkSoft, fontFamily: T.mono, fontWeight: 600 }}>{a.compras}</b> compra{a.compras !== 1 ? 's' : ''} cotizada{a.compras !== 1 ? 's' : ''}.
              {' '}En promedio, <b style={{ color: T.inkSoft, fontFamily: T.mono, fontWeight: 600 }}>{a.pctPromedio}%</b> menos.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Desplegable de selección múltiple. El valor viaja como cadena separada por
// comas, que es lo que ya esperan los filtros del backend (mismo formato que
// empresa, sucursal o estado). Teclado: flechas para moverse, Espacio/Enter
// para marcar, Esc para cerrar.
export function MultiSelect({ value, onChange, opciones, placeholder, style, ariaLabel }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  const boton = useRef(null);
  const items = useRef([]);
  const sel = String(value || '').split(',').map(s => s.trim()).filter(Boolean);

  useEffect(() => {
    const h = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  useEffect(() => { if (open) items.current[0]?.focus(); }, [open]);

  const alternar = (v) => {
    const nuevo = sel.includes(v) ? sel.filter(x => x !== v) : [...sel, v];
    onChange(nuevo.join(','));
  };
  const cerrar = () => { setOpen(false); boton.current?.focus(); };
  const teclado = (e, i) => {
    const mover = (j) => { e.preventDefault(); items.current[(j + opciones.length) % opciones.length]?.focus(); };
    if (e.key === 'ArrowDown') mover(i + 1);
    else if (e.key === 'ArrowUp') mover(i - 1);
    else if (e.key === 'Home') mover(0);
    else if (e.key === 'End') mover(opciones.length - 1);
    else if (e.key === 'Escape') { e.preventDefault(); cerrar(); }
    else if (e.key === 'Tab') setOpen(false);
    else if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); alternar(opciones[i].value); }
  };

  const etiqueta = sel.length === 0
    ? placeholder
    : sel.length === 1
      ? (opciones.find(o => o.value === sel[0])?.label || sel[0])
      : `${sel.length} seleccionados`;

  return (
    <div ref={wrap} style={{ position: 'relative', ...style }}>
      <button type="button" ref={boton} onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox" aria-expanded={open} aria-label={ariaLabel ? `${ariaLabel}: ${etiqueta}` : undefined}
        onKeyDown={e => { if (e.key === 'ArrowDown' && !open) { e.preventDefault(); setOpen(true); } }}
        style={{
          ...SELECT_STYLE, outline: undefined, width: '100%', maxWidth: 'none', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', gap: 8, textAlign: 'left',
          color: sel.length ? T.ink : T.muted,
          borderColor: sel.length ? 'var(--plasma-line)' : T.field,
        }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{etiqueta}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true" style={{ flexShrink: 0, transition: 'transform 0.18s', transform: open ? 'rotate(180deg)' : 'none' }}>
          <path style={{ fill: 'var(--steel)' }} d="M7 10l5 5 5-5z" />
        </svg>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999, marginTop: 4,
          background: 'var(--panel)', border: `1px solid ${T.field}`, borderRadius: 4,
          boxShadow: '0 4px 24px rgba(0,0,0,0.10)', maxHeight: 300, overflowY: 'auto', minWidth: 230,
        }}>
          {sel.length > 0 && (
            <button type="button" className="db-plain db-row" onClick={() => onChange('')}
              style={{ padding: '8px 12px', fontSize: 12.5, fontWeight: 600, color: T.primaryText, borderBottom: `1px solid ${T.line}` }}>
              Quitar selección ({sel.length})
            </button>
          )}
          <ul role="listbox" aria-multiselectable="true" aria-label={ariaLabel || placeholder} style={{ listStyle: 'none', margin: 0, padding: '4px 0' }}>
            {opciones.map((o, i) => {
              const marcado = sel.includes(o.value);
              return (
                <li key={o.value} ref={el => { items.current[i] = el; }} role="option" aria-selected={marcado} tabIndex={-1}
                  className="db-option" onClick={() => alternar(o.value)} onKeyDown={e => teclado(e, i)}>
                  <span aria-hidden="true" style={{
                    width: 14, height: 14, borderRadius: 3, flexShrink: 0, display: 'grid', placeItems: 'center',
                    border: `1px solid ${marcado ? T.primary : T.field}`, background: marcado ? T.primary : 'var(--well)',
                  }}>
                    {marcado && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" style={{ stroke: 'var(--panel)' }} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 5 5 9-10" /></svg>}
                  </span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.label}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

// Banda del tablero: agrupa tarjetas bajo una pregunta que el usuario se hace.
function Banda({ id, titulo, nota, children }) {
  return (
    <section aria-labelledby={id} style={{ marginBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 14, paddingBottom: 10, borderBottom: `1px solid ${T.line}` }}>
        <h2 id={id} style={{ margin: 0, fontFamily: T.label, fontSize: 19, fontWeight: 600, color: T.primary, letterSpacing: '0.14em', textTransform: 'uppercase', lineHeight: 1 }}>{titulo}</h2>
        {nota && <span style={{ fontSize: 12.5, color: T.inkSoft }}>{nota}</span>}
      </div>
      {children}
    </section>
  );
}

const GRID = (min) => ({ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${min}px), 1fr))`, gap: 16, alignItems: 'start' });

// Etapas del ciclo de una solicitud, en el orden en que avanza. Las rechazadas
// no entran: el backend las excluye salvo que se pidan con el filtro de estado.
const ETAPAS = ['pendiente', 'aprobada', 'por_pagar', 'pagada'];

// Filtros del tablero guardados mientras dure la pestaña: al volver de otra
// pantalla, el tablero sigue donde se dejó.
const LLAVE_FILTROS = 'dashboardFiltros';
// Roles cuyo trabajo diario es su cola: abren en "Al día de hoy"
const PRIMERO_HOY = ['contador', 'programacion'];
const COLUMNA_DIAS = { aging: 'Días esperando', backlog: 'Días en estado', proyeccion: 'Vence' };
const textoDias = (tarjeta, d) => {
  if (d == null) return 'Sin fecha';
  if (tarjeta !== 'proyeccion') return `${d} d`;
  return d < 0 ? `hace ${-d} d` : d === 0 ? 'hoy' : `en ${d} d`;
};
const leerGuardado = () => { try { return JSON.parse(sessionStorage.getItem(LLAVE_FILTROS)) || null; } catch { return null; } };

export default function Dashboard({ onNavigate }) {
  const guardado = useRef(leerGuardado()).current;
  const { user } = useAuth();
  const windowW = useWindowW();
  const isMobile = windowW < 640;
  const [filtersOpen, setFiltersOpen] = useState(false);
  // En escritorio solo se ve la fila de fechas; el resto se despliega con "Más filtros"
  const [masFiltros, setMasFiltros] = useState(guardado?.masFiltros || false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [recarga, setRecarga] = useState(0);
  const [actualizado, setActualizado] = useState(null);
  const [catalogos, setCatalogos] = useState({ empresas: [], sucursales: [], motivos: [] });

  // El dashboard abre filtrado a la semana en curso: es lo que se consulta a
  // diario y evita traer todo el histórico en la primera carga. Desde ahí se
  // amplía con los presets o eligiendo otra semana. La banda "Al día de hoy"
  // no depende de este rango.
  const [preset, setPreset] = useState(guardado?.preset ?? ''); // ningún preset activo: manda la semana
  const [filtros, setFiltros] = useState(() => {
    const hoy = new Date();
    const { desde, hasta } = rangoSemana(hoy.getFullYear(), numeroSemana(hoy));
    const base = { desde, hasta, empresa: '', sucursal: '', motivo: '', estado: '', direccion: '', tipo: '', proveedor: '', cuentaAgrupadora: '' };
    if (guardado?.filtros) Object.keys(base).forEach(k => { if (typeof guardado.filtros[k] === 'string') base[k] = guardado.filtros[k]; });
    // "Ver detalle" del tablero de Dirección General deja aquí el filtro que
    // estaba viendo, para no obligar a rearmarlo a mano. Se consume una sola vez.
    try {
      const raw = localStorage.getItem('dashboardFiltroInicial');
      if (raw) {
        localStorage.removeItem('dashboardFiltroInicial');
        const heredado = JSON.parse(raw);
        // Sólo las claves que este tablero conoce: lo demás se ignora
        Object.keys(base).forEach(k => { if (heredado[k] !== undefined && heredado[k] !== null) base[k] = heredado[k]; });
      }
    } catch {}
    return base;
  });
  // Cierre de pagos: solo para quien paga o supervisa el pago. Respeta la compañía filtrada.
  const veCierre = ['admin', 'finanzas', 'programacion'].includes(user?.rol);
  const [cierre, setCierre] = useState(null);
  useEffect(() => {
    if (!veCierre) return;
    const qs = filtros.empresa ? `?empresa=${encodeURIComponent(filtros.empresa)}` : '';
    api.get(`/indicadores/cierre${qs}`).then(setCierre).catch(() => setCierre(null));
  }, [veCierre, filtros.empresa]);
  // Filtro rápido por semana del año (año + número de semana → desde/hasta)
  const [semanaAnio, setSemanaAnio] = useState(guardado?.semanaAnio ?? new Date().getFullYear());
  const [semanaNum, setSemanaNum] = useState(() => (guardado ? guardado.semanaNum ?? '' : String(numeroSemana())));
  useEffect(() => {
    try { sessionStorage.setItem(LLAVE_FILTROS, JSON.stringify({ filtros, preset, semanaAnio, semanaNum, masFiltros })); } catch {}
  }, [filtros, preset, semanaAnio, semanaNum, masFiltros]);
  // Por defecto solo se ofrecen las semanas cercanas a hoy; desplegar las 53 de
  // golpe obliga a buscar la actual en una lista larguísima. Con "ver todas" se
  // abren las demás para consultar cualquier semana del año.
  const [todasLasSemanas, setTodasLasSemanas] = useState(false);
  // Alertas de presupuesto rebasado del mes en curso (admin/finanzas/director; el resto recibe 403 y se ignora)
  const [alertasPresupuesto, setAlertasPresupuesto] = useState([]);

  useEffect(() => {
    api.get('/presupuestos/estado').then(d => setAlertasPresupuesto(d.alertas || [])).catch(() => {});
  }, []);

  // Drill-down: lista de órdenes al presionar la lupa de una barra
  const [drill, setDrill] = useState({ open: false, campo: '', label: '', valor: '', loading: false, error: false, items: [], total: 0 });

  // Catálogos para los selects (una sola vez)
  useEffect(() => {
    Promise.all([
      api.get('/empresas').catch(() => []),
      api.get('/sucursales').catch(() => []),
    ]).then(([emp, suc]) => setCatalogos(c => ({
      ...c,
      empresas: emp, // objetos completos: { nombre, sucursales, ... }
      sucursales: suc.map(s => s.nombre),
    })));
  }, []);

  // El dropdown de motivos sigue al tipo de solicitud filtrado: débito y contribuciones
  // tienen catálogo propio; el resto (pago/reembolso/finiquito) usan /motivos.
  // Con varios tipos seleccionados no hay un catálogo único que aplique, así que
  // se ofrece el general; el catálogo propio solo se usa al filtrar por un tipo.
  useEffect(() => {
    const CATALOGO_POR_TIPO = {
      debito: '/motivos-debito',
      contribuciones: '/motivos-contribuciones',
      activos_fijos: '/motivos-activos-fijos',
      mercancias_nacionales: '/motivos-mercancias-nacionales',
      nomina: '/motivos-nomina',
    };
    const sel = String(filtros.tipo || '').split(',').map(s => s.trim()).filter(Boolean);
    const ep = sel.length === 1 ? (CATALOGO_POR_TIPO[sel[0]] || '/motivos') : '/motivos';
    api.get(ep)
      .then(mot => setCatalogos(c => ({ ...c, motivos: mot.map(m => m.nombre) })))
      .catch(() => {});
  }, [filtros.tipo]);

  // Datos filtrados — con debounce 300ms para no disparar API en cada tecla
  const [filtrosDelay, setFiltrosDelay] = useState(filtros);
  useEffect(() => {
    const t = setTimeout(() => setFiltrosDelay(filtros), 300);
    return () => clearTimeout(t);
  }, [filtros]);

  useEffect(() => {
    setLoading(true);
    const qs = Object.entries(filtrosDelay)
      .filter(([, v]) => v)
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
      .join('&');
    api.get(`/dashboard/analytics${qs ? `?${qs}` : ''}`)
      .then(d => { setData(d); setError(false); setActualizado(new Date()); })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [filtrosDelay, recarga]);

  const setFiltro = (k, v) => setFiltros(f => ({ ...f, [k]: v }));
  const toggleFiltro = (k) => (v) => setFiltros(f => ({ ...f, [k]: f[k] === v ? '' : v }));

  // Para los filtros de multiselección: suma o quita un valor de la lista en vez
  // de reemplazarla, para que al hacer clic en una barra no se pierda el resto.
  const alternarEnLista = (k) => (v) => setFiltros(f => {
    const sel = String(f[k] || '').split(',').map(s => s.trim()).filter(Boolean);
    const nuevo = sel.includes(v) ? sel.filter(x => x !== v) : [...sel, v];
    return { ...f, [k]: nuevo.join(','), ...(k === 'tipo' ? { motivo: '' } : {}) };
  });

  // Al cambiar de empresa, limpia la sucursal si no pertenece a la nueva empresa
  const cambiarEmpresa = (v) => setFiltros(f => {
    const emp = catalogos.empresas.find(e => e.nombre === v);
    const sucValida = !v || !emp?.sucursales?.length || emp.sucursales.includes(f.sucursal);
    return { ...f, empresa: v, sucursal: sucValida ? f.sucursal : '' };
  });
  const toggleEmpresa = (v) => cambiarEmpresa(filtros.empresa === v ? '' : v);

  // Sucursales visibles según empresa seleccionada (si no tiene asignadas, todas)
  const empresaObj = catalogos.empresas.find(e => e.nombre === filtros.empresa);
  const sucursalesVisibles = empresaObj?.sucursales?.length
    ? catalogos.sucursales.filter(s => empresaObj.sucursales.includes(s))
    : catalogos.sucursales;

  const aplicarPreset = (id) => {
    setPreset(id);
    setSemanaNum('');
    const { desde, hasta } = rangoPreset(id);
    setFiltros(f => ({ ...f, desde, hasta }));
  };

  const aplicarSemana = (anio, num) => {
    setSemanaAnio(anio);
    setSemanaNum(num);
    if (!num) return;
    setPreset('');
    const { desde, hasta } = rangoSemana(anio, parseInt(num, 10));
    setFiltros(f => ({ ...f, desde, hasta }));
  };

  // Semanas que ofrece el desplegable: las dos anteriores, la actual y las dos
  // siguientes. En un año que no es el actual no hay "semana de hoy" que centrar,
  // así que se listan todas.
  const anioEnCurso = new Date().getFullYear();
  const semanaDeHoy = numeroSemana();
  const semanasOpciones = useMemo(() => {
    const todas = Array.from({ length: 53 }, (_, i) => i + 1);
    if (todasLasSemanas || Number(semanaAnio) !== anioEnCurso) return todas;
    const cercanas = todas.filter(n => Math.abs(n - semanaDeHoy) <= 2);
    // Si hay una semana elegida fuera de la ventana, se conserva para que el
    // desplegable no aparezca vacío al volver a abrirlo.
    const sel = parseInt(semanaNum, 10);
    if (sel && !cercanas.includes(sel)) return [...cercanas, sel].sort((a, b) => a - b);
    return cercanas;
  }, [todasLasSemanas, semanaAnio, anioEnCurso, semanaDeHoy, semanaNum]);

  const limpiar = () => {
    setPreset('todo');
    setSemanaNum('');
    setFiltros({ desde: '', hasta: '', empresa: '', sucursal: '', motivo: '', estado: '', direccion: '', tipo: '', proveedor: '', cuentaAgrupadora: '' });
  };

  const hayFiltros = Object.values(filtros).some(Boolean);

  // Abre el modal con las órdenes que componen el valor seleccionado, respetando
  // los filtros activos del dashboard (fechas, empresa, etc.).
  const pedirDrill = (estado, params) => {
    setDrill({ ...estado, params, open: true, loading: true, error: false, items: [], total: 0 });
    const qs = Object.entries(params)
      .filter(([, v]) => v)
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
      .join('&');
    api.get(`/dashboard/drilldown${qs ? `?${qs}` : ''}`)
      .then(r => setDrill(d => ({ ...d, loading: false, items: r.items || [], total: r.total || 0, montoTotal: r.montoTotal })))
      .catch(() => setDrill(d => ({ ...d, loading: false, error: true })));
  };
  const cargarDrill = (campo, label, valor) => pedirDrill({ campo, label, valor, titulo: null, dias: null }, { ...filtros, [campo]: valor });
  const abrirDrill = (campo, label) => (valor) => cargarDrill(campo, label, valor);
  // Tramo de una tarjeta de "Al día de hoy": foto de hoy, así que sin fechas ni estado
  const abrirTramo = (tarjeta, titulo) => (tramo, etiqueta) => {
    const { desde, hasta, estado, ...resto } = filtros;
    pedirDrill({ titulo: `${titulo} · ${etiqueta}`, dias: tarjeta }, { ...resto, tarjeta, tramo });
  };

  // Un folio abre su solicitud en Historial General (que la busca y abre el detalle)
  const abrirFolio = (folio) => {
    try { localStorage.setItem('historialFolioInicial', folio); } catch {}
    setDrill(d => ({ ...d, open: false }));
    onNavigate?.('historial-general');
  };
  const Folio = ({ folio }) => onNavigate ? (
    <button type="button" className="db-plain" onClick={() => abrirFolio(folio)} title="Abrir en Historial General"
      style={{ width: 'auto', fontFamily: T.mono, fontSize: 12, color: T.primary, fontWeight: 500, textDecoration: 'underline', textDecorationColor: 'var(--plasma-line)', textUnderlineOffset: 3 }}>
      {folio}
    </button>
  ) : <span style={{ fontFamily: T.mono, fontSize: 12, color: T.ink, fontWeight: 500 }}>{folio}</span>;
  const cerrarDrill = () => setDrill(d => ({ ...d, open: false }));

  const chips = [
    filtros.empresa  && { k: 'empresa',  nombre: 'Compañía', valor: filtros.empresa },
    filtros.sucursal && { k: 'sucursal', nombre: 'Sucursal', valor: filtros.sucursal },
    filtros.motivo   && { k: 'motivo',   nombre: 'Motivo', valor: filtros.motivo },
    filtros.direccion && { k: 'direccion', nombre: 'Dirección', valor: sinPrefijoDireccion(filtros.direccion) },
    filtros.proveedor && { k: 'proveedor', nombre: 'Proveedor', valor: filtros.proveedor },
    filtros.cuentaAgrupadora && { k: 'cuentaAgrupadora', nombre: 'Categoría', valor: filtros.cuentaAgrupadora.split(',').map(s => s.trim()).filter(Boolean).join(' · ') },
    filtros.estado   && { k: 'estado',   nombre: 'Estado', valor: ESTADO_LABEL[filtros.estado] || filtros.estado },
    filtros.tipo     && { k: 'tipo', nombre: 'Tipo', valor: filtros.tipo.split(',').map(s => s.trim()).filter(Boolean).map(t => (TIPO_SOLICITUD_META[t] || {}).label || t).join(' · ') },
  ].filter(Boolean);

  const porEstadoMap = useMemo(() => {
    const map = {};
    (data?.porEstado || []).forEach(d => { map[d._id] = d; });
    return map;
  }, [data]);

  const totalMonto = data?.totales?.monto || 0;
  const totalCount = data?.totales?.count || 0;
  const compTotal = (data?.comprobaciones || []).reduce((s, d) => ({ count: s.count + d.count, monto: s.monto + d.monto }), { count: 0, monto: 0 });
  const prevMonto = data?.totalesPrev?.monto ?? null;

  // Cómo se nombra el rango en pantalla: el mismo texto encabeza la banda del
  // periodo y el resumen, para que nadie confunda "creadas esta semana" con "hoy".
  const { desde, hasta } = filtrosDelay;
  const rangoTexto = desde && hasta
    ? `Creadas del ${fechaCorta(desde)} al ${fechaCorta(hasta, true)}`
    : desde ? `Creadas desde el ${fechaCorta(desde, true)}`
    : hasta ? `Creadas hasta el ${fechaCorta(hasta, true)}`
    : 'Todo el histórico';
  const diasRango = desde && hasta ? Math.round((new Date(hasta) - new Date(desde)) / 86400000) + 1 : null;

  const porTipo = (data?.porTipo || [])
    .filter(d => d.count > 0)
    .sort((a, b) => b.monto - a.monto);

  // Las dos primeras bandas cambian de orden según el rol (ver PRIMERO_HOY)
  const bandaPeriodo = data && (
    <Banda id="banda-periodo" titulo="Solicitudes del periodo" nota={`${rangoTexto}. Presiona una etapa para filtrar por ella.`}>
      <div style={{ ...CARD, display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))' }}>
        <div style={{ gridColumn: '1 / -1', padding: isMobile ? '18px 16px' : '22px 24px', borderBottom: `1px solid ${T.line}`, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0 }}>
            <div className="nx-label" style={{ marginBottom: 14 }}>Total solicitado</div>
            <Nixie value={`$${fmtEntero(totalMonto)}`} size={isMobile ? 30 : windowW < 1280 ? 46 : 58} label={`Total solicitado: $${fmtEntero(totalMonto)} MXN`} />
          </div>
          <div style={{ textAlign: isMobile ? 'left' : 'right' }}>
            <div style={{ fontSize: 13, color: T.inkSoft }}><span style={{ fontFamily: T.mono, color: T.ink }}>{totalCount}</span> solicitudes</div>
            <Tendencia actual={totalMonto} previo={prevMonto} texto={diasRango ? `vs los ${diasRango} días previos` : undefined} />
          </div>
        </div>
        {ETAPAS.map((e, i) => {
          const d = porEstadoMap[e] || { count: 0, monto: 0 };
          const pct = totalMonto > 0 ? (d.monto / totalMonto) * 100 : 0;
          const ultima = i === ETAPAS.length - 1;
          return (
            <button key={e} type="button" className="db-plain db-cell" aria-pressed={filtros.estado === e}
              onClick={() => toggleFiltro('estado')(e)}
              style={{
                padding: '18px 20px', minWidth: 0,
                borderRight: ultima || (isMobile && i % 2 === 1) ? 'none' : `1px solid ${T.line}`,
                borderTop: 'none',
                borderBottom: isMobile && i < 2 ? `1px solid ${T.line}` : 'none',
              }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, marginBottom: 10, minHeight: 28 }}>
                <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: ESTADO_META[e].color, flexShrink: 0, transform: 'translateY(-1px)' }} />
                <span className="nx-label" style={{ lineHeight: 1.3 }}>{ESTADO_LABEL[e]}</span>
              </div>
              <div style={{ lineHeight: 1.1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><Nixie value={`$${fmtEntero(d.monto)}`} size={isMobile ? 20 : 23} tubos={false} /></div>
              <div style={{ fontSize: 12, color: T.muted, marginTop: 6 }}>
                <span style={{ fontFamily: T.mono }}>{d.count}</span> sol. · <span style={{ fontFamily: T.mono }}>{pct.toFixed(0)}%</span>
              </div>
            </button>
          );
        })}
      </div>
      <div style={{ fontSize: 12, color: T.inkSoft, margin: '8px 2px 0' }}>
        Las rechazadas no se suman aquí; para revisarlas, elige "Rechazada" en el filtro de estado.
      </div>
    </Banda>
  );
  const bandaHoy = data && (
    <Banda id="banda-hoy" titulo="Al día de hoy" nota="Todo lo abierto, sin importar cuándo se creó. Respeta compañía, tipo y los demás filtros.">
      <div style={GRID(300)}>
        <ProyeccionPagoCard data={data.proyeccionPago} onDrill={abrirTramo('proyeccion', 'Proyección de pagos')} />
        <BacklogPagoCard data={data.agingBacklog} aprobada={data.backlogAprobada} porPagar={data.backlogPorPagar} onDrill={abrirTramo('backlog', 'Backlog de pago')} />
        <AgingCard data={data.aging} onDrill={abrirTramo('aging', 'Validación contable')} />
        {veCierre && <CierrePagosCard data={cierre} onAbrir={onNavigate ? () => onNavigate('cierre-pagos') : undefined} />}
      </div>
    </Banda>
  );

  return (
    <div>
      {/* Resumen del periodo (el título "Dashboard" ya lo pone la barra superior) */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', margin: '0 0 16px' }}>
      <p style={{ margin: 0, fontSize: 13, color: T.inkSoft }} aria-live="polite">
        {loading ? 'Actualizando…' : (
          <>
            <span style={{ color: T.ink, fontWeight: 600 }}>{rangoTexto}</span>
            {' · '}<span style={{ fontFamily: T.mono }}>{totalCount}</span> solicitudes · <span style={{ fontFamily: T.mono }}>${fmt(totalMonto)}</span> MXN
            {actualizado && <> · actualizado a las <span style={{ fontFamily: T.mono }}>{actualizado.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}</span></>}
          </>
        )}
      </p>
      {/* Análisis con IA. Sólo el admin lo ve; la ruta del backend además exige
          rol admin, porque esconder el botón no protege nada. */}
      {user?.rol === 'admin' && <AsistenteIA filtros={filtros} />}
      </div>

      {/* ── Alerta: presupuestos rebasados este mes ── */}
      {alertasPresupuesto.length > 0 && (
        <div role="alert" style={{ padding: '12px 16px', marginBottom: 16, borderRadius: 6, border: `1px solid ${T.errorLine}`, background: T.errorBg }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: T.errorText, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icono d={ICONO_ALERTA} size={16} />
            Presupuesto rebasado este mes ({alertasPresupuesto.length})
          </div>
          <div style={{ fontSize: 12.5, color: T.inkSoft, display: 'flex', flexWrap: 'wrap', gap: '4px 20px' }}>
            {alertasPresupuesto.slice(0, 6).map((a, i) => (
              <span key={i}>
                {a.tipo === 'direccion' ? sinPrefijoDireccion(a.nombre) : a.nombre}:{' '}
                <b style={{ fontFamily: T.mono, fontWeight: 600, color: T.errorText }}>${fmtEntero(a.gastado)}</b>
                <span style={{ fontFamily: T.mono }}> / ${fmtEntero(a.presupuesto)}</span>
              </span>
            ))}
            {alertasPresupuesto.length > 6 && <span>… y {alertasPresupuesto.length - 6} más (ver Presupuestos)</span>}
          </div>
        </div>
      )}

      {/* ── Barra de filtros ── */}
      <div style={{ ...CARD, overflow: 'visible', padding: '14px 16px', marginBottom: 28 }} role="search" aria-label="Filtros del tablero">
        {/* Toggle de filtros en móvil */}
        {isMobile && (
          <button type="button" onClick={() => setFiltersOpen(o => !o)} aria-expanded={filtersOpen} style={{
            width: '100%', padding: '9px 12px', borderRadius: 6, border: `1px solid ${T.field}`,
            background: filtersOpen ? T.primaryTint : 'var(--well)',
            color: filtersOpen ? T.primaryText : T.inkSoft,
            fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: filtersOpen ? 12 : 0,
          }}>
            <span>Filtros {hayFiltros ? `(${Object.values(filtros).filter(Boolean).length} activos)` : ''}</span>
            <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true" style={{ transition: 'transform 0.18s', transform: filtersOpen ? 'rotate(180deg)' : 'none' }}><path style={{ fill: 'var(--steel)' }} d="M7 10l5 5 5-5z" /></svg>
          </button>
        )}
        {(!isMobile || filtersOpen) && (<>
          {/* Fila 1: cuándo */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
            <div role="group" aria-label="Periodo rápido" style={{ display: 'flex', flexWrap: 'wrap', gap: 2, background: T.canvas, border: `1px solid ${T.line}`, padding: 2, borderRadius: 6 }}>
              {PRESETS.map(p => (
                <button type="button" key={p.id} onClick={() => aplicarPreset(p.id)} aria-pressed={preset === p.id}
                  style={{
                    padding: '5px 12px', borderRadius: 4, border: 'none', cursor: 'pointer', fontSize: 12.5, fontWeight: 600,
                    background: preset === p.id ? 'var(--panel-hi)' : 'transparent',
                    color: preset === p.id ? T.primaryText : T.inkSoft,
                    boxShadow: preset === p.id ? 'inset 0 -2px var(--plasma)' : 'none',
                  }}>
                  {p.label}
                </button>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
              <input type="date" aria-label="Desde" value={filtros.desde} onChange={e => { setPreset(''); setSemanaNum(''); setFiltro('desde', e.target.value); }}
                style={{ ...SELECT_STYLE, minWidth: 0, width: isMobile ? '100%' : 136, fontFamily: T.mono }} />
              <span style={{ color: T.muted, display: 'inline-grid' }}><IconoUI nombre="derecha" size={14} /></span>
              <input type="date" aria-label="Hasta" value={filtros.hasta} onChange={e => { setPreset(''); setSemanaNum(''); setFiltro('hasta', e.target.value); }}
                style={{ ...SELECT_STYLE, minWidth: 0, width: isMobile ? '100%' : 136, fontFamily: T.mono }} />
            </div>

            <div style={{ display: 'flex', gap: 6, alignItems: 'center', width: isMobile ? '100%' : undefined }}>
              <select aria-label="Año de la semana" value={semanaAnio} onChange={e => aplicarSemana(parseInt(e.target.value, 10), semanaNum)}
                style={{ ...SELECT_STYLE, minWidth: 0, width: isMobile ? '40%' : 88 }}>
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(a => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
              <select aria-label="Semana" value={semanaNum}
                onChange={e => {
                  // Opción centinela: abre el resto del año sin filtrar nada todavía
                  if (e.target.value === '__todas') { setTodasLasSemanas(true); return; }
                  aplicarSemana(semanaAnio, e.target.value);
                }}
                style={{ ...SELECT_STYLE, minWidth: 0, maxWidth: 'none', width: isMobile ? '60%' : 210 }}>
                <option value="">Semana</option>
                {semanasOpciones.map(n => (
                  <option key={n} value={n}>
                    {etiquetaSemana(semanaAnio, n)}
                    {Number(semanaAnio) === anioEnCurso && n === semanaDeHoy ? ' · actual' : ''}
                  </option>
                ))}
                {!todasLasSemanas && Number(semanaAnio) === anioEnCurso && (
                  <option value="__todas">── Ver todas las semanas ──</option>
                )}
              </select>
            </div>
            {!isMobile && (
              <button type="button" className="nx-ghost" aria-expanded={masFiltros} onClick={() => setMasFiltros(v => !v)}
                style={{ padding: '7px 12px', fontSize: 12.5, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 7, color: masFiltros || chips.length ? T.primary : undefined, borderColor: chips.length ? 'var(--plasma-line)' : undefined }}>
                <IconoUI nombre="filtro" size={14} /> {masFiltros ? 'Menos filtros' : 'Más filtros'}{chips.length ? ` (${chips.length})` : ''}
              </button>
            )}
            {hayFiltros && isMobile && (
              <button type="button" onClick={limpiar} className="nx-ghost"
                style={{ padding: '7px 14px', fontSize: 12.5, fontWeight: 600, width: '100%' }}>
                Limpiar filtros
              </button>
            )}
          </div>

          {/* Fila 2: qué (plegable en escritorio) */}
          {(masFiltros || isMobile) && (<div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginTop: 10 }}>
            <select aria-label="Compañía" value={filtros.empresa} onChange={e => cambiarEmpresa(e.target.value)} style={{ ...SELECT_STYLE, width: isMobile ? '100%' : undefined, maxWidth: isMobile ? 'none' : SELECT_STYLE.maxWidth }}>
              <option value="">Todas las compañías</option>
              {catalogos.empresas.map(e => <option key={e._id || e.nombre} value={e.nombre}>{e.nombre}</option>)}
            </select>

            <select aria-label="Sucursal" value={filtros.sucursal} onChange={e => setFiltro('sucursal', e.target.value)} style={{ ...SELECT_STYLE, width: isMobile ? '100%' : undefined, maxWidth: isMobile ? 'none' : SELECT_STYLE.maxWidth }}>
              <option value="">{filtros.empresa ? `Sucursales de ${filtros.empresa}` : 'Todas las sucursales'}</option>
              {sucursalesVisibles.map(s => <option key={s} value={s}>{s}</option>)}
            </select>

            <select aria-label="Motivo de pago" value={filtros.motivo} onChange={e => setFiltro('motivo', e.target.value)} style={{ ...SELECT_STYLE, width: isMobile ? '100%' : undefined, maxWidth: isMobile ? 'none' : SELECT_STYLE.maxWidth }}>
              <option value="">Todos los motivos</option>
              {catalogos.motivos.map(m => <option key={m} value={m}>{m}</option>)}
            </select>

            <select aria-label="Dirección" value={filtros.direccion} onChange={e => setFiltro('direccion', e.target.value)} style={{ ...SELECT_STYLE, width: isMobile ? '100%' : undefined, maxWidth: isMobile ? 'none' : SELECT_STYLE.maxWidth }}>
              <option value="">Todas las direcciones</option>
              {(data?.direcciones || []).map(d => <option key={d} value={d}>{sinPrefijoDireccion(d)}</option>)}
            </select>

            <select aria-label="Estado" value={filtros.estado} onChange={e => setFiltro('estado', e.target.value)} style={{ ...SELECT_STYLE, minWidth: 120, width: isMobile ? '100%' : undefined, maxWidth: isMobile ? 'none' : SELECT_STYLE.maxWidth }}>
              <option value="">Todos los estados</option>
              {Object.keys(ESTADO_UI).map(e => <option key={e} value={e}>{ESTADO_LABEL[e]}</option>)}
            </select>

            <MultiSelect
              value={filtros.tipo}
              ariaLabel="Tipo de solicitud"
              onChange={v => setFiltros(f => ({ ...f, tipo: v, motivo: '' }))}
              opciones={Object.entries(TIPO_SOLICITUD_META).map(([t, m]) => ({ value: t, label: m.label }))}
              placeholder="Todos los tipos"
              style={{ minWidth: 170, width: isMobile ? '100%' : 180 }}
            />

            <MultiSelect
              value={filtros.cuentaAgrupadora}
              ariaLabel="Categoría de gasto"
              onChange={v => setFiltro('cuentaAgrupadora', v)}
              opciones={(data?.categoriasGasto || []).map(c => ({ value: c, label: c }))}
              placeholder="Todas las categorías"
              style={{ minWidth: 180, width: isMobile ? '100%' : 190 }}
            />

            {!isMobile && hayFiltros && chips.length === 0 && (
              <button type="button" onClick={limpiar} className="nx-ghost" style={{ padding: '7px 14px', fontSize: 12.5, fontWeight: 600 }}>Limpiar filtros</button>
            )}
          </div>)}

          {chips.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 12 }}>
              {chips.map(c => (
                <button type="button" key={c.k} className="db-chip" onClick={() => setFiltro(c.k, '')} aria-label={`Quitar filtro ${c.nombre}: ${c.valor}`}>
                  <span style={{ color: T.muted, fontWeight: 500 }}>{c.nombre}:</span> {c.valor} <span aria-hidden="true"><IconoUI nombre="cerrar" size={10} stroke={2.2} /></span>
                </button>
              ))}
              {!isMobile && (
                <button type="button" onClick={limpiar} className="nx-ghost" style={{ padding: '4px 10px', fontSize: 12.5, fontWeight: 600, marginLeft: 4 }}>Limpiar filtros</button>
              )}
            </div>
          )}
        </>)}
      </div>

      {error && (
        <div role="alert" style={{ ...CARD, padding: 32, textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: T.ink, marginBottom: 4 }}>No se pudo cargar el tablero</div>
          <div style={{ fontSize: 12.5, color: T.muted, marginBottom: 14 }}>Puede ser la conexión o el servidor. Tus filtros se conservan.</div>
          <button type="button" onClick={() => setRecarga(n => n + 1)}
            className="nx-plate" style={{ width: 'auto', display: 'inline-flex', padding: '9px 22px' }}>
            Reintentar
          </button>
        </div>
      )}
      {loading && !data && <div style={{ padding: 60, display: 'flex', justifyContent: 'center' }}><Spinner /></div>}

      {data && !error && (
        <div style={{ opacity: loading ? 0.55 : 1, transition: 'opacity 0.15s' }} aria-busy={loading}>

          {PRIMERO_HOY.includes(user?.rol) ? <>{bandaHoy}{bandaPeriodo}</> : <>{bandaPeriodo}{bandaHoy}</>}

          {/* ── 3. A dónde va el dinero ── */}
          <Banda id="banda-dinero" titulo="A dónde va el dinero" nota="Presiona una fila para filtrar todo el tablero; la lupa abre sus órdenes.">
            <div style={{ marginBottom: 16 }}>
              <AreaChart serie={data.serie} granularidad={data.granularidad} ancho={windowW >= 1100 ? 1180 : 720} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <RepartoGasto dataGlobal={data} filtrosGlobal={filtrosDelay} recarga={recarga} isMobile={isMobile}
                onAplicar={(ls) => setFiltros(f => ({ ...f, ...Object.fromEntries(ls.map(l => [l.filtro, l.valor])) }))} />
            </div>
            <div style={{ ...GRID(250), alignItems: 'stretch' }}>
              <BarList titulo="Gasto por tipo de solicitud" data={porTipo} limite={5}
                etiqueta={t => (TIPO_SOLICITUD_META[t] || {}).label || t}
                activo={filtros.tipo} onToggle={alternarEnLista('tipo')}
                pie={compTotal.count > 0 && (
                  <div style={{ padding: '10px 16px', fontSize: 12, color: T.muted, borderTop: `1px solid ${T.line}` }}>
                    Aparte, comprobaciones de gastos: <span style={{ fontFamily: T.mono, color: T.inkSoft }}>{compTotal.count}</span> por <span style={{ fontFamily: T.mono, color: T.inkSoft }}>${fmt(compTotal.monto)}</span>
                  </div>
                )} />
              <BarList titulo="Gasto por compañía" data={data.porEmpresa} limite={5}
                activo={filtros.empresa} onToggle={toggleEmpresa} onDrill={abrirDrill('empresa', 'Compañía')} />
              <BarList titulo="Gasto por categoría" data={data.porCuentaAgrupadora} limite={5}
                activo={filtros.cuentaAgrupadora} onToggle={alternarEnLista('cuentaAgrupadora')} onDrill={abrirDrill('cuentaAgrupadora', 'Categoría de gasto')} />
              <BarList titulo="Gasto por motivo de pago" data={data.porMotivo} limite={5}
                activo={filtros.motivo} onToggle={toggleFiltro('motivo')} onDrill={abrirDrill('motivo', 'Motivo de pago')} />
              <BarList titulo="Gasto por sucursal" data={data.porSucursal} limite={5}
                activo={filtros.sucursal} onToggle={toggleFiltro('sucursal')} onDrill={abrirDrill('sucursal', 'Sucursal')} />
              <BarList titulo="Gasto por proveedor" data={data.topProveedores} limite={5}
                activo={filtros.proveedor} onToggle={toggleFiltro('proveedor')} onDrill={abrirDrill('proveedor', 'Proveedor')}
                alerta={concentracion(data.topProveedores, 'proveedor')} />
              <BarList titulo="Gasto por dirección" data={data.porDireccion} etiqueta={sinPrefijoDireccion} limite={5}
                activo={filtros.direccion} onToggle={toggleFiltro('direccion')} onDrill={abrirDrill('direccion', 'Dirección')} />
              {/* Solo lectura: el backend no filtra por solicitante */}
              <BarList titulo="Gasto por solicitante" data={data.porSolicitante} limite={5} />
            </div>
          </Banda>

          {/* ── 4. Cómo va el proceso ── */}
          <Banda id="banda-proceso" titulo="Cómo va el proceso" nota={rangoTexto}>
            <div style={{ ...GRID(300), marginBottom: 16 }}>
              <CicloCard ciclo={data.ciclo} />
              <AhorroCard ahorro={data.ahorro} />
              <div style={CARD}>
                <div style={CARD_TITLE}>
                  <h3 style={H3}>Solicitudes recientes</h3>
                  {hayFiltros && <span style={CARD_NOTE}>según filtros</span>}
                </div>
                {data.recientes.length === 0 ? <Vacio>Sin solicitudes en el periodo</Vacio> : data.recientes.map((s, i) => (
                  <div key={s._id || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: `1px solid ${T.line}` }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <Folio folio={s.folio} />
                        <EstadoBadge estado={s.estado} />
                      </div>
                      <div title={`${s.proveedor} · ${s.empresa}${s.sucursal ? ` · ${s.sucursal}` : ''}`} style={{ fontSize: 12, color: T.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>
                        {s.proveedor} · {s.empresa}{s.sucursal ? ` · ${s.sucursal}` : ''}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 500, color: T.ink, fontFamily: T.mono }}>${fmt(s.monto)}</div>
                      <div style={{ fontSize: 11.5, color: T.muted, fontFamily: T.mono }}>{new Date(s.createdAt).toLocaleDateString('es-MX')}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <AprobadoresCard data={data.porAprobador} />
          </Banda>
        </div>
      )}

      {/* ── Drill-down: órdenes de un valor seleccionado ── */}
      <Modal open={drill.open} onClose={cerrarDrill}
        title={drill.titulo || `${drill.label}: ${drill.campo === 'direccion' ? sinPrefijoDireccion(drill.valor) : drill.valor}`} width={780}>
        <div>
          {!drill.error && (
            <div style={{ fontSize: 12.5, color: T.muted, marginBottom: 12 }}>
              {drill.loading ? 'Cargando órdenes…' : (
                <>
                  <b style={{ color: T.ink, fontFamily: T.mono, fontWeight: 600 }}>{drill.total}</b> {drill.total === 1 ? 'orden' : 'órdenes'}
                  {' · '}
                  <b style={{ color: T.ink, fontFamily: T.mono, fontWeight: 600 }}>
                    ${fmt(drill.montoTotal ?? drill.items.reduce((s, x) => s + (x.monto || 0), 0))}
                  </b>
                  {' MXN'}
                  {/* La lista se corta en 200, pero el monto es siempre el de todas */}
                  {drill.total > drill.items.length && (
                    <span> · se muestran <span style={{ fontFamily: T.mono }}>{drill.items.length}</span>; el monto es de todas</span>
                  )}
                  {hayFiltros && <span> · según filtros activos</span>}
                </>
              )}
            </div>
          )}

          {drill.loading ? (
            <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}><Spinner /></div>
          ) : drill.error ? (
            <div role="alert" style={{ padding: 28, textAlign: 'center' }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: T.ink, marginBottom: 4 }}>No se pudieron cargar las órdenes</div>
              <div style={{ fontSize: 12.5, color: T.muted, marginBottom: 14 }}>Revisa la conexión e inténtalo de nuevo.</div>
              <button type="button" onClick={() => pedirDrill(drill, drill.params)}
                className="nx-plate" style={{ width: 'auto', display: 'inline-flex', padding: '9px 22px' }}>
                Reintentar
              </button>
            </div>
          ) : drill.items.length === 0 ? (
            <Vacio pad={30}>No hay órdenes con este valor y los filtros activos.</Vacio>
          ) : (
            <div style={{ maxHeight: 440, overflow: 'auto', border: `1px solid ${T.line}`, borderRadius: 6 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                <thead>
                  <tr>
                    {['Folio', 'Proveedor', 'Compañía', 'Sucursal', 'Monto', 'Estado', ...(drill.dias ? [COLUMNA_DIAS[drill.dias]] : []), 'Fecha'].map(h => (
                      <th key={h} scope="col" style={{ padding: '8px 12px', textAlign: h === 'Monto' ? 'right' : 'left', whiteSpace: 'nowrap', fontFamily: T.label, fontSize: 12.5, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', background: T.tableHead, color: T.muted, position: 'sticky', top: 0 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {drill.items.map((s, i) => (
                    <tr key={s._id || i} style={{ borderBottom: `1px solid ${T.line}` }}>
                      <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}><Folio folio={s.folio} /></td>
                      <td style={{ padding: '8px 12px', color: T.inkSoft, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.proveedor}>{s.proveedor}</td>
                      <td style={{ padding: '8px 12px', color: T.inkSoft, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.empresa}>{s.empresa}</td>
                      <td style={{ padding: '8px 12px', color: T.muted, whiteSpace: 'nowrap' }}>{s.sucursal || '—'}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 500, color: T.ink, fontFamily: T.mono, whiteSpace: 'nowrap' }}>
                        ${fmt(s.monto)}
                        {/* En moneda extranjera el importe va convertido (así cuadra con la
                            tarjeta), y debajo el original con el tipo de cambio aplicado */}
                        {(s.moneda || 'MXN') !== 'MXN' && (
                          <div style={{ fontSize: 11.5, color: T.muted }}>
                            {s.moneda} {fmt(s.montoOriginal)} × {fmt(s.tipoCambioAplicado)}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '8px 12px' }}><EstadoBadge estado={s.estado} /></td>
                      {drill.dias && (
                        <td style={{ padding: '8px 12px', whiteSpace: 'nowrap', fontFamily: T.mono, fontSize: 12, color: (drill.dias === 'proyeccion' ? s.dias < 0 : s.dias > 15) ? T.errorText : T.ink }}>
                          {textoDias(drill.dias, s.dias)}
                        </td>
                      )}
                      <td style={{ padding: '8px 12px', color: T.muted, whiteSpace: 'nowrap', fontFamily: T.mono, fontSize: 12 }}>{new Date(s.createdAt).toLocaleDateString('es-MX')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
